/**
 * **The Ankiety tab's whole answer, from `features/survey-report`'s two reads.** The stream on the
 * left and the pane's own form on the right, projected from one response.
 *
 * ── The READS are the feature's; the projections are this tab's ──────────────────────────────
 * `useSurveyHistorySource` owns `GET /customer/{id}/surveys` and the exercise batch beside it,
 * because `/raporty`'s report dialog needs the same pair off the same cache entry. What stays here
 * is what only this tab draws: the stream, and the chart strip's own rule.
 *
 * ── Two projections, one read, one hook ──────────────────────────────────────────────────────
 * The stream is every occurrence of every form; the pane is one form in full. They answer different
 * questions off the same rows, and splitting them across two hooks would mean two components each
 * holding half the tab's state and a second `select` over the same query. They are built side by
 * side here so the tab has ONE `pending` and one `failed`.
 *
 * ── The chart strip is USUALLY the pane's own form, and sometimes it is not ───────────────────
 * Owner, 16 September 2026: the strip follows the occurrence in the pane only while that form has a
 * cadence. Selecting an intake leaves the strip on the last cadenced form it showed, because an
 * intake is asked once and charting it throws away the trend the tab exists for.
 * `chartStripOccurrenceId` is that rule. It costs a SECOND projection only in the case it is about:
 * when the two resolve to the same occurrence — every other press — the same built view is handed
 * back for both.
 *
 * ── Why the projection lives in a hook and not in the component ──────────────────────────────
 * `buildSurveyHistory` is the expensive call on this screen — it walks every question of every
 * visible occurrence and mints a cell for each — and the React Compiler could not memoise it where
 * it stood. The chain `toSurveyHistorySource → exerciseIds → new Map → buildSurveyHistory` was
 * interleaved with later hook calls (`usePublishTabMeta`, `useMarkReviewed`, a `useCallback`), and
 * a mutable range that crosses a hook boundary is a bailout: the compiler cannot prove the values
 * are unchanged, so the build re-ran on every pin toggle, every dialog step, every keystroke in a
 * date field and every flip of `marking` — and each run minted fresh `points` arrays, so every
 * chart on the strip recomputed with them.
 *
 * Here there is NO hook call after the build, so the whole chain compiles to one guarded scope
 * keyed on the four things it actually depends on — which is why the reads are one hook, called
 * first, rather than two queries interleaved with the projections.
 */

import { useTranslation } from 'react-i18next';

import { useSurveyHistoryViews, type SurveyHistoryView } from '~/features/survey-report';

import { buildSurveyStream, type SurveyStreamView } from '../model/surveyStream';
import type { TrendsRange } from '../model/trendsRange';

export interface SurveyHistoryState {
  /** The pane's form, in full — every occurrence of it, never narrowed by the range. */
  readonly view: SurveyHistoryView;
  /** The chart strip's form. The same object as `view` unless the pane is on a one-off form. */
  readonly strip: SurveyHistoryView;
  /** The left-hand list: every occurrence of every form, newest first. */
  readonly stream: SurveyStreamView;
  /** Either read is still in flight. One flag, because the tab is one answer. */
  readonly pending: boolean;
  readonly failed: boolean;
}

export interface SurveyHistoryQuery {
  /** The range bar's window. It reaches the CHARTS and nothing else — see `model/surveyHistory`. */
  readonly chartRange: TrendsRange;
  /** The occurrence the pane is showing. `null` opens on the newest answered one. */
  readonly occurrenceId: number | null;
  /** Which form chip the stream is filtered by. `null` is „Wszystkie". */
  readonly chipId: string | null;
  /**
   * The cadenced occurrence the chart strip was last moved to, or `null` before the trainer has
   * moved it. See `chartStripOccurrenceId` — the cadence is re-checked there.
   */
  readonly lastCadencedId: number | null;
}

export function useSurveyHistory(
  clientId: number,
  query: SurveyHistoryQuery,
): SurveyHistoryState {
  const { t, i18n } = useTranslation();
  // The report and the strip are `features/survey-report`'s, shared with the review inbox.
  const { view, strip, source, pending, failed } = useSurveyHistoryViews(clientId, query);

  return {
    view,
    strip,
    stream: buildSurveyStream(source, { t, locale: i18n.language, chipId: query.chipId }),
    pending,
    failed,
  };
}
